import { lazy, Suspense, type ComponentType } from 'react';
import { Navigate, type RouteObject } from 'react-router';
import { Spinner } from './components/ui';
import {
  RedirectIfSignedIn,
  RequireMfaPending,
  RequireSession,
  RootLayout,
} from './session/session';

// Route-level code splitting (UI-PERF-1): each screen is its own chunk.
function page(load: () => Promise<{ default: ComponentType }>) {
  const Page = lazy(load);
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center">
          <Spinner />
        </div>
      }
    >
      <Page />
    </Suspense>
  );
}

export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    children: [
      {
        element: <RedirectIfSignedIn />,
        children: [
          { path: '/login', element: page(() => import('./routes/auth/Login')) },
          { path: '/register', element: page(() => import('./routes/auth/Register')) },
          { path: '/forgot-password', element: page(() => import('./routes/auth/ForgotPassword')) },
        ],
      },
      {
        element: <RequireMfaPending />,
        children: [{ path: '/2fa', element: page(() => import('./routes/auth/TwoFactor')) }],
      },
      { path: '/verify-email', element: page(() => import('./routes/auth/VerifyEmail')) },
      {
        path: '/confirm-recipient',
        element: page(() => import('./routes/auth/ConfirmRecipient')),
      },
      { path: '/reset-password', element: page(() => import('./routes/auth/ResetPassword')) },
      {
        element: <RequireSession />,
        children: [
          {
            element: page(() => import('./routes/app/AppFrame')),
            children: [
              { path: '/', element: page(() => import('./routes/app/Dashboard')) },
              { path: '/onboarding', element: page(() => import('./routes/app/Onboarding')) },
              { path: '/invite', element: page(() => import('./routes/app/AcceptInvite')) },
              { path: '/account/security', element: page(() => import('./routes/app/Security')) },
              { path: '/account/profile', element: page(() => import('./routes/app/Profile')) },
              {
                path: '/_platform-admin',
                element: page(() => import('./routes/admin/PlatformAdmin')),
              },
              {
                path: '/_platform-admin/feedback/:feedbackId',
                element: page(() => import('./routes/admin/PlatformAdmin')),
              },
              { path: '/feedback', element: page(() => import('./routes/app/Feedback')) },
              {
                path: '/feedback/:feedbackId',
                element: page(() => import('./routes/app/Feedback')),
              },
              {
                path: '/sites/:tenantId',
                element: page(() => import('./routes/app/SiteLayout')),
                children: [
                  { index: true, element: page(() => import('./routes/app/SiteOverview')) },
                  { path: 'team', element: page(() => import('./routes/app/Team')) },
                  { path: 'leads', element: page(() => import('./routes/app/site/Leads')) },
                  { path: 'billing', element: page(() => import('./routes/app/site/Billing')) },
                  { path: 'domains', element: page(() => import('./routes/app/site/Domains')) },
                  {
                    path: 'analytics',
                    element: page(() => import('./routes/app/site/Analytics')),
                  },
                  { path: 'pages', element: page(() => import('./routes/app/site/Pages')) },
                  {
                    path: 'pages/:pageId',
                    element: page(() => import('./routes/app/site/PageEditor')),
                  },
                  { path: 'menus', element: page(() => import('./routes/app/site/Menus')) },
                  { path: 'media', element: page(() => import('./routes/app/site/Media')) },
                  { path: 'settings', element: page(() => import('./routes/app/site/Settings')) },
                  {
                    path: 'publishing',
                    element: page(() => import('./routes/app/site/Publishing')),
                  },
                  // u44: the old addresses open their tab in "การเผยแพร่".
                  {
                    path: 'versions',
                    element: <Navigate to="../publishing?tab=versions" relative="path" replace />,
                  },
                  {
                    path: 'redirects',
                    element: <Navigate to="../publishing?tab=redirects" relative="path" replace />,
                  },
                  {
                    path: 'integrations',
                    element: page(() => import('./routes/app/site/Integrations')),
                  },
                ],
              },
            ],
          },
        ],
      },
      { path: '*', element: page(() => import('./routes/NotFound')) },
    ],
  },
];
